import { useId, useState } from "react";
import { Check, Copy, KeyRound, PlugZap, RotateCw, Trash2, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";

import { apiClient } from "@/api/client";
import { useMcpToken } from "@/hooks/useMcpToken";
import {
  MCP_CLIENTS,
  getMcpClientSnippet,
  type McpClientId,
} from "@/lib/mcp-client-config";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

type McpTokenAction = "create" | "rotate" | "revoke";

interface McpAccessSectionProps {
  isActiveSubscriber: boolean;
}

interface RevealedCredential {
  serverUrl: string;
  token: string;
}

const ACTION_CONTENT: Record<
  McpTokenAction,
  { title: string; description: string; confirmLabel: string; destructive: boolean }
> = {
  create: {
    title: "Create your MCP token?",
    description:
      "Podwise will show the token once. Save it somewhere secure before dismissing the next screen.",
    confirmLabel: "Create token",
    destructive: false,
  },
  rotate: {
    title: "Rotate your MCP token?",
    description:
      "Every existing Podwise MCP connection will stop working immediately. You will need to update each client with the replacement token.",
    confirmLabel: "Rotate token",
    destructive: true,
  },
  revoke: {
    title: "Revoke MCP access?",
    description:
      "Every existing Podwise MCP connection will stop working. You can create a new token later if your subscription is active.",
    confirmLabel: "Revoke access",
    destructive: true,
  },
};

const formatTimestamp = (value: string | null) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString();
};

const mutationErrorMessage = (error: unknown, action: McpTokenAction) => {
  const message = error instanceof Error ? error.message : "";
  if (message.includes("409")) {
    return "An MCP token already exists. Refresh this page or rotate the existing token.";
  }
  if (message.includes("403")) {
    return "An active subscription is required to create or rotate an MCP token.";
  }
  return `We couldn't ${action} MCP access right now. Please try again.`;
};

async function copyToClipboard(value: string): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function ClientSetup({ serverUrl }: { serverUrl: string }) {
  const [activeClient, setActiveClient] = useState<McpClientId>("openclaw");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const tabId = useId();
  const selectedClient = MCP_CLIENTS.find(client => client.id === activeClient) ?? MCP_CLIENTS[0]!;
  const snippet = getMcpClientSnippet(activeClient, serverUrl);

  const handleCopy = async () => {
    const copied = await copyToClipboard(snippet);
    setCopyState(copied ? "copied" : "failed");
  };

  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="MCP client setup"
        className="flex gap-2 overflow-x-auto pb-2"
      >
        {MCP_CLIENTS.map(client => {
          const isSelected = client.id === activeClient;
          return (
            <button
              key={client.id}
              type="button"
              role="tab"
              id={`${tabId}-${client.id}-tab`}
              aria-selected={isSelected}
              aria-controls={`${tabId}-panel`}
              onClick={() => {
                setActiveClient(client.id);
                setCopyState("idle");
              }}
              className={cn(
                "shrink-0 rounded-lg border-2 border-gray-900 px-3 py-2 text-sm font-semibold transition-colors dark:border-gray-300",
                isSelected
                  ? `${theme.colors.primaryOrange} text-gray-900 dark:text-white`
                  : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700",
              )}
            >
              {client.label}
            </button>
          );
        })}
      </div>

      <div
        id={`${tabId}-panel`}
        role="tabpanel"
        aria-labelledby={`${tabId}-${activeClient}-tab`}
        className="space-y-3"
      >
        <p className="text-sm text-gray-600 dark:text-gray-300">{selectedClient.description}</p>
        <div className="relative">
          <pre className="max-h-80 overflow-auto rounded-xl border-2 border-gray-900 bg-gray-950 p-4 pr-14 text-xs leading-6 text-gray-100 dark:border-gray-300">
            <code>{snippet}</code>
          </pre>
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="absolute right-3 top-3 rounded-lg border-2 border-gray-300 bg-gray-800 p-2 text-gray-100 transition-colors hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-orange"
            aria-label={`Copy ${selectedClient.label} configuration`}
          >
            {copyState === "copied" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
          {copyState === "copied"
            ? "Configuration copied."
            : copyState === "failed"
              ? "Copy failed. Select the configuration and copy it manually."
              : "Replace <YOUR_TOKEN> through your environment or secret manager."}
        </p>
      </div>
    </div>
  );
}

function ConfirmationDialog({
  action,
  isWorking,
  onCancel,
  onConfirm,
}: {
  action: McpTokenAction;
  isWorking: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const content = ACTION_CONTENT[action];

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mcp-confirmation-title"
        className={cn(theme.cards.base, "w-full max-w-md p-6")}
      >
        <h2 id="mcp-confirmation-title" className="text-xl font-bold text-gray-900 dark:text-white">
          {content.title}
        </h2>
        <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">{content.description}</p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isWorking}
            className={cn(theme.buttons.secondary, "rounded-xl px-5 py-3 font-semibold", isWorking && "opacity-60")}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={isWorking}
            className={cn(
              content.destructive ? theme.buttons.destructive : theme.buttons.primary,
              "rounded-xl px-5 py-3 font-semibold",
              isWorking && "cursor-wait opacity-60",
            )}
          >
            {isWorking ? "Working…" : content.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function TokenRevealDialog({
  credential,
  onSaved,
}: {
  credential: RevealedCredential;
  onSaved: () => void;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  const handleCopyToken = async () => {
    const copied = await copyToClipboard(credential.token);
    setCopyState(copied ? "copied" : "failed");
  };

  return (
    <div className="fixed inset-0 z-70 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mcp-token-title"
        className={cn(theme.cards.base, "my-auto w-full max-w-3xl p-6 md:p-8")}
      >
        <div className="flex items-start gap-3">
          <KeyRound className="mt-1 h-7 w-7 shrink-0 text-primary-orange-dark dark:text-primary-orange" />
          <div>
            <h2 id="mcp-token-title" className="text-2xl font-bold text-gray-900 dark:text-white">
              Save this token now
            </h2>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Podwise cannot show this secret again. If you lose it, rotate the credential and update every client.
            </p>
          </div>
        </div>

        <div className={cn(theme.colors.warmYellowBg, theme.borders.neubrutalism, "mt-6 rounded-xl p-4")}>
          <div className="flex items-start justify-between gap-3">
            <code className="min-w-0 break-all text-sm font-semibold text-gray-900 dark:text-gray-100">
              {credential.token}
            </code>
            <button
              type="button"
              onClick={() => void handleCopyToken()}
              className={cn(theme.buttons.secondary, "shrink-0 rounded-lg p-2")}
              aria-label="Copy MCP token"
            >
              {copyState === "copied" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300" aria-live="polite">
            {copyState === "copied"
              ? "Token copied."
              : copyState === "failed"
                ? "Copy failed. Select the token and copy it manually."
                : "This token grants access to your Podwise MCP tools."}
          </p>
        </div>

        <div className="mt-7">
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Connect a client</h3>
          <ClientSetup serverUrl={credential.serverUrl} />
        </div>

        <div className="mt-7 flex justify-end">
          <button
            type="button"
            onClick={onSaved}
            className={cn(theme.buttons.primary, "rounded-xl px-6 py-3 font-semibold")}
          >
            I saved it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function McpAccessSection({ isActiveSubscriber }: McpAccessSectionProps) {
  const { mcpTokenStatus, isLoading, isError, refreshMcpToken } = useMcpToken();
  const [pendingAction, setPendingAction] = useState<McpTokenAction | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [revealedCredential, setRevealedCredential] = useState<RevealedCredential | null>(null);
  const credential = mcpTokenStatus?.credential ?? null;

  const handleConfirmedAction = async () => {
    if (!pendingAction) return;

    const action = pendingAction;
    setIsMutating(true);
    setMutationError(null);

    try {
      if (action === "revoke") {
        await apiClient.revokeMcpToken();
        setPendingAction(null);
        return;
      }

      const response = action === "create"
        ? await apiClient.createMcpToken()
        : await apiClient.rotateMcpToken();

      setRevealedCredential({
        serverUrl: response.server_url,
        token: response.token,
      });
      setPendingAction(null);
    } catch (error) {
      setMutationError(mutationErrorMessage(error, action));
      setPendingAction(null);
    } finally {
      await refreshMcpToken().catch(() => undefined);
      setIsMutating(false);
    }
  };

  return (
    <>
      <section className={cn(theme.cards.base, "p-6")}>
        <div className="flex items-start gap-4">
          <div className={cn(theme.colors.warmYellow, "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl")}>
            <PlugZap className="h-6 w-6 text-gray-900 dark:text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Connect your AI agent</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Give OpenClaw, Codex, Claude Code, Cursor, or another MCP client access to your Podwise podcasts.
            </p>
          </div>
        </div>

        {mutationError ? (
          <div
            className={cn(theme.borders.error, theme.colors.errorBg, theme.text.error, "mt-6 rounded-xl p-4 text-sm")}
            role="alert"
          >
            {mutationError}
          </div>
        ) : null}

        <div className="mt-6">
          {isLoading ? (
            <div className="flex items-center gap-3 py-5 text-sm text-gray-500 dark:text-gray-400">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-orange dark:border-gray-600 dark:border-t-primary-orange-dark" />
              Checking MCP access…
            </div>
          ) : isError || !mcpTokenStatus ? (
            <div className={cn(theme.borders.error, theme.colors.errorBg, "rounded-xl p-4") }>
              <p className={cn(theme.text.error, "text-sm")}>We couldn't load your MCP access status.</p>
              <button
                type="button"
                onClick={() => void refreshMcpToken()}
                className={cn(theme.buttons.secondary, "mt-3 rounded-lg px-4 py-2 text-sm font-semibold")}
              >
                Try again
              </button>
            </div>
          ) : credential ? (
            <div className={cn(theme.borders.neubrutalism, "rounded-xl bg-white p-5 dark:bg-gray-800")}>
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={cn(theme.colors.primaryOrange, "h-3 w-3 rounded-full")} aria-hidden="true" />
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {isActiveSubscriber ? "MCP access is active" : "MCP token is paused"}
                    </p>
                  </div>
                  <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Created</dt>
                      <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                        {formatTimestamp(credential.created_at)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Last used</dt>
                      <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                        {formatTimestamp(credential.last_used_at)}
                      </dd>
                    </div>
                  </dl>
                </div>
                <div className="flex flex-wrap gap-3">
                  {isActiveSubscriber ? (
                    <button
                      type="button"
                      onClick={() => setPendingAction("rotate")}
                      className={cn(theme.buttons.secondary, "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold")}
                    >
                      <RotateCw className="h-4 w-4" />
                      Rotate
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setPendingAction("revoke")}
                    className={cn(theme.buttons.destructive, "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold")}
                  >
                    <Trash2 className="h-4 w-4" />
                    Revoke
                  </button>
                </div>
              </div>

              {credential.active_token_count > 1 ? (
                <div className={cn(theme.colors.warmYellowBg, "mt-5 flex gap-3 rounded-lg border-2 border-gray-900 p-3 text-sm dark:border-gray-300")}>
                  <TriangleAlert className="h-5 w-5 shrink-0" />
                  <p>
                    {credential.active_token_count} legacy tokens are active. Rotating or revoking replaces all of them together.
                  </p>
                </div>
              ) : null}

              {!isActiveSubscriber ? (
                <p className="mt-5 text-sm text-gray-600 dark:text-gray-300">
                  Your existing token cannot be used or rotated without an active subscription. You can still revoke it here.{" "}
                  <Link to="/paywall" className="font-semibold underline underline-offset-2">
                    Choose a plan
                  </Link>
                </p>
              ) : null}
            </div>
          ) : (
            <div className={cn(theme.colors.warmYellowBg, theme.borders.neubrutalism, "rounded-xl p-5")}>
              <p className="font-semibold text-gray-900 dark:text-white">No MCP token yet</p>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Create one credential and use it in every agent you trust. Podwise only shows the secret once.
              </p>
              {isActiveSubscriber ? (
                <button
                  type="button"
                  onClick={() => setPendingAction("create")}
                  className={cn(theme.buttons.primary, "mt-4 inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold")}
                >
                  <KeyRound className="h-4 w-4" />
                  Create MCP token
                </button>
              ) : (
                <Link
                  to="/paywall"
                  className={cn(theme.buttons.primary, "mt-4 inline-flex rounded-xl px-5 py-3 font-semibold")}
                >
                  Choose a plan to connect
                </Link>
              )}
            </div>
          )}
        </div>

        {mcpTokenStatus?.server_url ? (
          <div className="mt-8 border-t-2 border-gray-300 pt-6 dark:border-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Client setup</h3>
            <p className="mb-4 mt-1 text-sm text-gray-600 dark:text-gray-300">
              The saved token cannot be revealed here. Use the placeholder below, or rotate the token to receive a replacement.
            </p>
            <ClientSetup serverUrl={mcpTokenStatus.server_url} />
          </div>
        ) : null}

        <div className="mt-7 flex gap-3 rounded-xl border-2 border-gray-900 bg-gray-100 p-4 text-sm leading-6 text-gray-700 dark:border-gray-300 dark:bg-gray-700 dark:text-gray-200">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <p>
            This token is a password with access to your Podwise MCP tools. It can read podcast data, create or delete sessions, and make billable asks that count against your plan. Only give it to clients you trust.
            Client configuration files, your clipboard, and shell history can expose it, so prefer an environment variable backed by a secret manager.
          </p>
        </div>
      </section>

      {pendingAction ? (
        <ConfirmationDialog
          action={pendingAction}
          isWorking={isMutating}
          onCancel={() => setPendingAction(null)}
          onConfirm={handleConfirmedAction}
        />
      ) : null}

      {revealedCredential ? (
        <TokenRevealDialog
          credential={revealedCredential}
          onSaved={() => setRevealedCredential(null)}
        />
      ) : null}
    </>
  );
}
